import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { formatApiErrorDetail } from "../lib/api";
import { ArrowRight } from "lucide-react";
import Logo from "../components/Logo";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const justReset = new URLSearchParams(window.location.search).get("reset") === "1";
  const [paused] = useState(() => { const p = sessionStorage.getItem("vc_paused") === "1"; sessionStorage.removeItem("vc_paused"); return p; });
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      await login(email, password);
      nav("/dashboard");
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex bg-white">
      <div className="hidden lg:flex flex-col justify-between w-[44%] border-r border-zinc-200 bg-zinc-50 p-12">
        <div className="flex items-center gap-2">
          <Logo size={36} />
          <div className="text-xl font-black tracking-tighter">VIZIONCAST</div>
        </div>
        <div>
          <div className="swiss-rule mb-6"></div>
          <h2 className="text-4xl xl:text-5xl font-black tracking-tighter leading-[0.95] mb-6">
            Cast your<br/>vision.<br/>Consistently.
          </h2>
          <p className="text-base text-zinc-600 max-w-md">
            One platform where the messy daily musings of a leader become the steady,
            on-brand rhythm of content their company, customers, and team can feel.
          </p>
        </div>
        <div className="grid grid-cols-3 gap-px bg-zinc-200">
          {[
            { k: "01", l: "Capture musings" },
            { k: "02", l: "VizionCast drafts on-brand" },
            { k: "03", l: "Schedule + ship" },
          ].map((c) => (
            <div key={c.k} className="bg-zinc-50 p-4">
              <div className="text-[10px] tracking-[0.25em] uppercase text-[#0033FF] font-bold">{c.k}</div>
              <div className="text-sm font-semibold mt-2">{c.l}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <div className="text-xs tracking-[0.25em] uppercase text-[#0033FF] font-bold mb-3">Sign in</div>
          <h1 className="text-4xl font-black tracking-tighter mb-2">Welcome back.</h1>
          <p className="text-sm text-zinc-600 mb-8">Pick up where your vision left off.</p>

          {paused && <div data-testid="login-paused-notice" className="text-sm text-[#FF3B30] border-l-2 border-[#FF3B30] pl-3 py-1 mb-5">This account is paused. Use "Ask Vizi" below to contact support.</div>}
          {justReset && <div data-testid="login-reset-success" className="text-sm text-[#0033FF] border-l-2 border-[#0033FF] pl-3 py-1 mb-5">Password updated. Sign in with your new password.</div>}
          <form onSubmit={submit} className="space-y-5">
            <div>
              <label className="text-[10px] tracking-[0.25em] uppercase font-bold text-zinc-600">Email</label>
              <input
                data-testid="login-email-input"
                type="email" value={email} onChange={(e)=>setEmail(e.target.value)} required
                className="w-full mt-2 px-3 py-3 border border-zinc-300 focus:outline-none focus:border-[#0033FF] focus:ring-2 focus:ring-[#0033FF]/20 text-base"
              />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <label className="text-[10px] tracking-[0.25em] uppercase font-bold text-zinc-600">Password</label>
                <Link to="/forgot-password" data-testid="forgot-password-link" className="text-xs font-bold text-[#0033FF] hover:underline">Forgot password?</Link>
              </div>
              <input
                data-testid="login-password-input"
                type="password" value={password} onChange={(e)=>setPassword(e.target.value)} required
                className="w-full mt-2 px-3 py-3 border border-zinc-300 focus:outline-none focus:border-[#0033FF] focus:ring-2 focus:ring-[#0033FF]/20 text-base"
              />
            </div>
            {err && <div data-testid="login-error" className="text-sm text-[#FF3B30] border-l-2 border-[#FF3B30] pl-3 py-1">{err}</div>}
            <button
              data-testid="login-submit-btn"
              disabled={loading}
              className="w-full bg-[#0033FF] text-white px-4 py-3.5 text-sm font-bold tracking-wider uppercase hover:bg-zinc-900 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading ? "Signing in…" : <>Continue <ArrowRight className="w-4 h-4" /></>}
            </button>
          </form>

          <div className="mt-8 text-sm text-zinc-600">
            New here? <Link to="/register" data-testid="goto-register" className="font-bold text-zinc-900 underline underline-offset-4">Create an account</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
